import { Box, Flex } from '@chakra-ui/react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

interface AgentChatMessageProps {
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
}

function AgentChatMessage({ role, content, isStreaming }: AgentChatMessageProps) {
  const isUser = role === 'user';

  // Normalize line breaks for markdown:
  // - Single \n between text → \n\n (paragraph break) so markdown renders them as separate paragraphs
  // - Preserve \n in lists (lines starting with - or * or digits)
  // - Preserve multiple \n (already paragraph breaks)
  const normalizedContent = content.replace(/([^\n])\n(?!\n)(?![-*\d])/g, '$1\n\n');

  return (
    <Flex justify={isUser ? 'flex-end' : 'flex-start'} mb={3}>
      <Box
        maxW="85%"
        px={4}
        py={2}
        borderRadius="lg"
        bg={isUser ? 'blue.500' : 'gray.700'}
        color="white"
        userSelect="text"
        cursor="text"
        sx={{
          '& p': { mb: 2, _last: { mb: 0 } },
          '& ul, & ol': { pl: 5, mb: 2 },
          '& li': { mb: 1 },
          '& strong': { fontWeight: 'bold' },
          '& em': { fontStyle: 'italic' },
          '& code': {
            bg: 'whiteAlpha.200',
            px: 1,
            py: 0.5,
            borderRadius: 'sm',
            fontSize: 'xs',
            fontFamily: 'mono',
          },
          '& pre': {
            bg: 'whiteAlpha.100',
            p: 3,
            borderRadius: 'md',
            overflowX: 'auto',
            mb: 2,
            '& code': { bg: 'transparent', p: 0 },
          },
          '& h1, & h2, & h3': { fontWeight: 'bold', mb: 1, mt: 2 },
          '& h1': { fontSize: 'md' },
          '& h2': { fontSize: 'sm' },
          '& h3': { fontSize: 'sm' },
          '& a': { color: 'blue.200', textDecoration: 'underline' },
          '& blockquote': {
            borderLeftWidth: '3px',
            borderLeftColor: 'whiteAlpha.400',
            pl: 3,
            ml: 0,
            fontStyle: 'italic',
            mb: 2,
          },
        }}
        fontSize="sm"
        lineHeight="tall"
      >
        <ReactMarkdown remarkPlugins={[remarkBreaks]}>{normalizedContent}</ReactMarkdown>
        {isStreaming && (
          <Box
            as="span"
            display="inline-block"
            w="2px"
            h="14px"
            bg="white"
            ml={0.5}
            animation="blink 1s step-end infinite"
          />
        )}
      </Box>
    </Flex>
  );
}

export default AgentChatMessage;
